import React, { useEffect, useRef } from 'react';
import { t } from '@lingui/macro';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@shared/api/supabaseClient';
import { queryKeys } from '@shared/lib/react-query';
import { useAuth } from '@shared/hooks/useAuth';
import { Bell } from 'lucide-react';
import { doctorAccountControlQueryKeys } from '@shared/hooks/api/useDoctorAccountControl';
import { doctorAccountControlApi } from '@shared/api/doctor-account-control';
import { notifyWithBrowserEffects } from '@shared/notifications/browserNotificationEffects';

// ─── Event payload shapes ─────────────────────────────────────────────────────

type NotificationCreatedPayload = {
  notificationId: string;
  unreadCount: number;
  category: string;
  priority: number;
  createdAt: string;
  toastEligible: boolean;
};

type NotificationReadPayload = {
  notificationId: string;
  unreadCount: number;
};

type NotificationCountChangedPayload = {
  unreadCount: number;
  trigger: 'mark_all_read' | 'archive' | 'consultation_fee_paid';
};

type DoctorControlEventPayload = {
  controlRequestId: string;
  doctorId: string;
  occurredAt: string;
};

type NotificationListCacheItem = {
  id: string;
  isRead?: boolean;
  readAt?: string | null;
};

type NotificationListCachePage = {
  items?: NotificationListCacheItem[];
};

type NotificationListCache = {
  pages?: NotificationListCachePage[];
};

// ─── Deduplication ────────────────────────────────────────────────────────────
// Module-level set: intentionally survives component remounts so the same
// notification never produces a duplicate toast across navigation events.

const seenNotificationIds = new Set<string>();

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useNotificationRealtime = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { user, isDoctorControlMode } = useAuth();
  const queryClient = useQueryClient();
  const notificationChannelRef = useRef<RealtimeChannel | null>(null);
  const doctorControlChannelRefs = useRef<RealtimeChannel[]>([]);
  const seenDoctorControlEventsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled || !user?.id) return;

    const topic = `notifications:${user.id}`;
    const channel = supabase.channel(topic, {
      config: { broadcast: { ack: false } },
    });
    notificationChannelRef.current = channel;

    const isRealDoctorSession = user.role === 'doctor' && !isDoctorControlMode;
    const refreshDoctorControlState = () => {
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorInbox(),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount() });
      if (isRealDoctorSession) {
        void queryClient.fetchQuery({
          queryKey: doctorAccountControlQueryKeys.doctorInbox(),
          queryFn: () => doctorAccountControlApi.getMyDoctorInbox(),
        });
      }
    };

    channel
      // ── notification.created ─────────────────────────────────────────────
      // Server pushes this after every new notification (or dedupe bump).
      // Invalidate the list + unread count, and optionally show a toast.
      .on('broadcast', { event: 'notification.created' }, ({ payload }) => {
        const event = payload as NotificationCreatedPayload;
        if (!event?.notificationId) return;

        // Deduplicate — the same notification can arrive more than once if
        // the user has multiple tabs open (each tab has its own subscription).
        if (seenNotificationIds.has(event.notificationId)) return;
        seenNotificationIds.add(event.notificationId);

        // Sync the badge count from the authoritative server value.
        queryClient.setQueryData(queryKeys.notifications.unreadCount(), {
          unreadCount: event.unreadCount,
        });

        // Invalidate the list so the new item appears when the panel opens.
        void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });
        if (isRealDoctorSession) {
          void queryClient.invalidateQueries({
            queryKey: doctorAccountControlQueryKeys.doctorInbox(),
          });
        }

        if (event.toastEligible) {
          const title = t({
            id: 'notifications.realtime.new-title',
            message: 'New notification',
          });
          const description = t({
            id: 'notifications.realtime.new-description',
            message: 'Open notifications to review the latest update.',
          });
          toast(title, {
            description,
            icon: React.createElement(Bell, { className: 'mt-0.5 h-5 w-5 text-white/80' }),
            duration: 4000,
          });
          notifyWithBrowserEffects({
            id: event.notificationId,
            title,
            body: description,
            tag: `notification:${event.category}`,
          });
        }
      })

      // ── notification.read ────────────────────────────────────────────────
      // Server pushes this when markAsRead is called. Handles cross-tab sync:
      // if the user reads a notification in another tab/window, this updates
      // the current tab's cache without a full refetch.
      .on('broadcast', { event: 'notification.read' }, ({ payload }) => {
        const event = payload as NotificationReadPayload;
        if (!event?.notificationId) return;

        // Update the specific item in the infinite list cache.
        queryClient.setQueriesData<NotificationListCache | undefined>(
          { queryKey: queryKeys.notifications.all },
          old => {
            if (!old?.pages) return old;
            const pages = old.pages.map(page => ({
              ...page,
              items: (page.items ?? []).map(item =>
                item.id === event.notificationId
                  ? { ...item, isRead: true, readAt: item.readAt ?? new Date().toISOString() }
                  : item
              ),
            }));
            return { ...old, pages };
          }
        );

        // Sync the badge from the server's authoritative count.
        queryClient.setQueryData(queryKeys.notifications.unreadCount(), {
          unreadCount: event.unreadCount,
        });
      })

      // ── notification.count_changed ───────────────────────────────────────
      // Server pushes this after markAllAsRead or archive. Handles cross-tab
      // sync for bulk operations — the list is refetched so archived items
      // disappear and all items show as read.
      .on('broadcast', { event: 'notification.count_changed' }, ({ payload }) => {
        const event = payload as NotificationCountChangedPayload;
        if (typeof event?.unreadCount !== 'number') return;

        // Sync the badge.
        queryClient.setQueryData(queryKeys.notifications.unreadCount(), {
          unreadCount: event.unreadCount,
        });

        if (event.trigger === 'mark_all_read') {
          // Optimistically mark all cached notifications as read so the UI
          // updates instantly without waiting for the refetch.
          queryClient.setQueriesData<NotificationListCache | undefined>(
            { queryKey: queryKeys.notifications.all },
            old => {
              if (!old?.pages) return old;
              const now = new Date().toISOString();
              const pages = old.pages.map(page => ({
                ...page,
                items: (page.items ?? []).map(item =>
                  item.isRead ? item : { ...item, isRead: true, readAt: item.readAt ?? now }
                ),
              }));
              return { ...old, pages };
            }
          );
        }

        if (event.trigger === 'archive' || event.trigger === 'consultation_fee_paid') {
          // Refetch list rows when payload/actions change or an item disappears.
          void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });
        }
      })

      .subscribe();

    if (isRealDoctorSession) {
      const doctorControlTopicIds = Array.from(
        new Set([user.id, user.as_doctor_id].filter((value): value is string => Boolean(value)))
      );

      const handleDoctorControlEvent = ({ payload }: { payload: unknown }) => {
        const event = payload as DoctorControlEventPayload;
        if (!event?.controlRequestId || !doctorControlTopicIds.includes(event.doctorId)) return;
        const eventKey = `${event.controlRequestId}:${event.occurredAt}`;
        if (seenDoctorControlEventsRef.current.has(eventKey)) return;
        seenDoctorControlEventsRef.current.add(eventKey);
        refreshDoctorControlState();
      };

      doctorControlChannelRefs.current = doctorControlTopicIds.map(topicId => {
        const doctorControlChannel = supabase.channel(`doctor-control:${topicId}`, {
          config: { private: true, broadcast: { ack: false } },
        });

        doctorControlChannel
          .on('broadcast', { event: 'doctor_control_requested' }, handleDoctorControlEvent)
          .on('broadcast', { event: 'doctor_control_started' }, handleDoctorControlEvent)
          .on('broadcast', { event: 'doctor_control_ended' }, handleDoctorControlEvent)
          .on('broadcast', { event: 'doctor_control_revoked' }, handleDoctorControlEvent)
          .subscribe();

        return doctorControlChannel;
      });
    }

    return () => {
      if (notificationChannelRef.current) {
        void supabase.removeChannel(notificationChannelRef.current);
        notificationChannelRef.current = null;
      }
      doctorControlChannelRefs.current.forEach(channel => {
        void supabase.removeChannel(channel);
      });
      doctorControlChannelRefs.current = [];
    };
  }, [enabled, isDoctorControlMode, queryClient, user?.as_doctor_id, user?.id, user?.role]);
};
